"use client";

import { useState } from "react";
import type { ActiveSpecial } from "@/lib/payload/public";
import { safeHref } from "@/components/public/safe-href";

/*
 * `announcement-banner / split-cta-bar` (Bold-Dark/Luxe).
 *
 * A confident, button-forward offer strip: a justified one-line bar with a
 * full-bleed solid `--color-brand-accent` fill, the offer message in
 * `--color-on-accent` on the left, and a tappable inverse-chip CTA hugging the
 * right gutter. An optional dismiss "X" hides the bar for the session.
 *
 * The saturated fill self-separates from the masthead, so no border — flat and
 * thin (no shadow, no radius on the band itself). On mobile the row collapses
 * to a centered stack (message, then CTA) with the dismiss pinned top-right.
 *
 * Session dismiss: the prior decision is read lazily from `sessionStorage` at
 * mount (guarded for SSR, where `window` is absent so the bar always renders),
 * mirroring the consent-banner pattern. Dismiss is session-scoped and keyed by
 * a hash of the promo text so a new promo re-appears.
 */
export function SpecialsBannerSplitCta({ special }: { special: ActiveSpecial }) {
  const storageKey = `specials-dismiss:${hashText(special.text)}`;
  const [dismissed, setDismissed] = useState(() => readDismissed(storageKey));

  if (dismissed) {
    return null;
  }

  const dismiss = () => {
    setDismissed(true);
    try {
      window.sessionStorage.setItem(storageKey, "1");
    } catch {
      // best-effort; in-memory state already hid the bar this view.
    }
  };

  const hasCta = Boolean(special.ctaHref && special.ctaLabel);

  return (
    <div className="relative bg-(--color-brand-accent)">
      <div className="mx-auto flex w-full max-w-5xl flex-col items-center gap-2 px-6 py-2.5 text-center md:flex-row md:justify-between md:gap-4 md:px-8 md:text-left">
        <p className="text-sm font-medium text-(--color-on-accent)">{special.text}</p>
        <div className="flex shrink-0 items-center gap-3">
          {hasCta ? (
            <a
              href={safeHref(special.ctaHref as string)}
              className="inline-flex items-center gap-1.5 rounded-(--radius-full) bg-(--color-surface-1) px-4 py-1.5 text-[13px] font-semibold text-(--color-brand-accent) shadow-(--shadow-card) transition-transform duration-200 ease-out hover:-translate-y-px"
            >
              {special.ctaLabel}
              <span aria-hidden="true">→</span>
            </a>
          ) : null}
          {/* Inline on desktop (after the CTA); pinned top-right on mobile. */}
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss announcement"
            className="absolute top-1.5 right-3 rounded-(--radius-full) p-1 text-base leading-none text-(--color-on-accent)/70 transition-colors hover:text-(--color-on-accent) md:static md:right-auto md:top-auto"
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>
      </div>
    </div>
  );
}

/** Reads the session-dismiss flag; returns false on the server (no `window`) or when blocked. */
function readDismissed(storageKey: string): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  try {
    return window.sessionStorage.getItem(storageKey) === "1";
  } catch {
    // sessionStorage can throw in private/blocked contexts — keep the bar shown.
    return false;
  }
}

/** Stable, dependency-free hash so the session key tracks the specific promo text. */
function hashText(text: string): string {
  let h = 0;
  for (let i = 0; i < text.length; i += 1) {
    h = (h << 5) - h + text.charCodeAt(i);
    h |= 0;
  }
  return (h >>> 0).toString(36);
}
